import { useCallback, useRef, useState } from "react";

import {
  isReadyBpmnFile,
  readBpmnFile,
  type ReadyBpmnFile,
  type SelectedBpmnFile,
} from "./read-bpmn-file";

/** プレビューの図の状態。描けたと分かるまでは確定させない。 */
export type PreviewStatus = "pending" | "ok" | "failed";

export type BpmnFileSelection = {
  /** 選んだファイル（使えないものも含む。理由の表示に使う）。 */
  readonly file: SelectedBpmnFile | null;
  /** 使えるファイルのときだけ入る。 */
  readonly ready: ReadyBpmnFile | null;
  /** プレビューの図の状態（DI＝レイアウト情報が無い図は描けない）。 */
  readonly previewStatus: PreviewStatus;
  /**
   * 選ぶたびに変わる番号。プレビュー部品の key に使って作り直す。bpmn-js の部品は
   * 一度描けないと別の XML を渡されても描き直さないため。
   */
  readonly version: number;
  /**
   * ファイルを読む。後から別のファイルを選ぶか外した場合は null を返す
   * （呼び出し側が古い結果で自動入力しないように）。
   */
  readonly select: (_file: File) => Promise<SelectedBpmnFile | null>;
  readonly clear: () => void;
  readonly markPreviewRendered: () => void;
  readonly markPreviewFailed: () => void;
};

/**
 * .bpmn の選択状態（新規作成と取り込みで共通）。
 *
 * 読み取りは非同期なので、大きいファイルの直後に小さいファイルを選ぶと読み終わる
 * 順が逆転する。選ぶたびに番号を振り、最後に選んだもの以外の結果は捨てる。
 * 外した（×）後に読み終わった結果も捨てる。
 */
export function useBpmnFileSelection(): BpmnFileSelection {
  const [file, setFile] = useState<SelectedBpmnFile | null>(null);
  const [previewStatus, setPreviewStatus] = useState<PreviewStatus>("pending");
  const [version, setVersion] = useState(0);
  const latest = useRef(0);

  const select = useCallback(async (picked: File) => {
    latest.current += 1;
    const ticket = latest.current;
    const result = await readBpmnFile(picked);
    if (ticket !== latest.current) return null;

    setFile(result);
    setPreviewStatus("pending");
    setVersion(ticket);
    return result;
  }, []);

  const clear = useCallback(() => {
    latest.current += 1;
    setFile(null);
    setPreviewStatus("pending");
    setVersion(latest.current);
  }, []);

  const markPreviewRendered = useCallback(() => setPreviewStatus("ok"), []);
  const markPreviewFailed = useCallback(() => setPreviewStatus("failed"), []);

  return {
    file,
    ready: isReadyBpmnFile(file) ? file : null,
    previewStatus,
    version,
    select,
    clear,
    markPreviewRendered,
    markPreviewFailed,
  };
}
